// ═══════════════════════════════════════════════════════════════════
//  TOQUM · STYLE MODULE
//  Techpack + PO sheet + Spec sheet upload with the SAME AI extraction
//  process and outcome as Nirvikalp StyleRegistry (ai_service.php),
//  including the PO extraction review modal before saving.
//  Plus: quality plan generation, daily production, inspection cut-off
//  gating, PO split, supplier links (packing list · production · PPM)
//  and the per-PO document gallery.
// ═══════════════════════════════════════════════════════════════════
import React, { useState, useMemo } from 'react';
import {
  T, CC, Page, CardRow, Split, uid, today, addDays, num, Section, Field, Grid, Btn, DataTable,
  TabBar, Tag, Badge, StatCard, DynamicTable, Collapse, Modal, Toast, printHTML, isManager, API_URL,
} from './Shared';
import {
  AreaChart, Area, BarChart, Bar, ComposedChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import {
  extractDocumentData, fileToBase64, mapExtractedPOs, mapExtractedBOM, mergeBOM,
  poLinesToMatrix, mapExtractedSpec,
} from './Extraction';

const STAGES = ['Cutting','Sewing','Washing','Finishing','Packing'];
export const CUTOFF_PCT = { 'Inline':10, 'Pre-Final':80, 'Final':100 };

export default function StyleModule({ db, setDb, saveToDB, currentUser }) {
  const TABS = ['STYLES & POs','CREATE STYLE','TECHPACK & SPEC','ALLOCATION','QUALITY PLAN',
                'DAILY PRODUCTION','PRODUCTION MONITOR','INSPECTION OFFER','SUPPLIER LINKS','DOCUMENTS'];
  const [tab, setTab] = useState('STYLES & POs');
  const [toast, setToast] = useState(null);
  const notify = (msg, type='info') => { setToast({ msg, type }); setTimeout(()=>setToast(null), 4500); };

  const styles = useMemo(()=>db.styles||[], [db.styles]); const pos = useMemo(()=>db.pos||[], [db.pos]);
  const suppliers = (db.suppliers||[]).filter(s=>s.approvalStatus==='Approved' || !s.approvalStatus).map(s=>s.name);
  const brands = (db.brands||[]).map(b=>b.name||b);
  const countries = (db.countries||[]).map(c=>c.name||c);

  const stageQty = (poNo, stage) => (db.productionLogs||[])
    .filter(l=>l.poNo===poNo && l.stage===stage).reduce((a,l)=>a+num(l.qty),0);

  // ═══ PO EXTRACTION REVIEW MODAL (StyleRegistry pattern) ═══
  const [pendingPOs, setPendingPOs] = useState(null);
  const [pendingCtx, setPendingCtx] = useState(null);

  const POReviewModal = () => {
    const [rows, setRows] = useState(()=>(pendingPOs||[]).map(p=>({ ...p })));
    if (!pendingPOs) return null;
    const confirm = () => {
      pendingCtx?.onConfirm?.(rows);
      setPendingPOs(null); setPendingCtx(null);
      notify(`${rows.length} PO line(s) accepted.`, 'success');
    };
    return (
      <Modal title="📋 Review Extracted PO Data — Edit Before Saving" width={1000}
        onClose={()=>{ setPendingPOs(null); setPendingCtx(null); }}
        actions={<>
          <Btn label="Discard" color="#64748b" onClick={()=>{ setPendingPOs(null); setPendingCtx(null); }} />
          <Btn label={`Accept ${rows.length} PO line(s)`} color={T.success} onClick={confirm} />
        </>}>
        <div style={{ fontSize:11.5, color:T.textSub, marginBottom:10 }}>
          AI extraction is a starting point, not a source of truth — check every line against the original
          PO sheet before accepting. Reference documents in the same file can contaminate results.
        </div>
        <DynamicTable dense
          columns={[
            { label:'PO No', key:'num' }, { label:'Colour', key:'col' },
            { label:'Qty', key:'qty', type:'number' }, { label:'Ex-Factory', key:'date', type:'date' },
            { label:'FOB', key:'fob' }, { label:'Destination', key:'dest' },
            { label:'Size Breakdown (S:10,M:20)', key:'sizeBreakdown', width:'220px' },
          ]}
          data={rows} setData={setRows} onAdd={()=>setRows([...rows,{ id:uid() }])} />
        <div style={{ fontSize:12, fontWeight:700 }}>
          Total extracted qty: {rows.reduce((a,r)=>a+num(r.qty),0).toLocaleString()}
        </div>
      </Modal>
    );
  };

  // ═══ CREATE STYLE ═══
  const CreateStyle = () => {
    const [f, setF] = useState({ styleNo:'', description:'', brand:'', season:'', product:'', supplier:'',
      poNo:'', poDate:today(), poQty:'', exFactory:'' });
    const [rows, setRows] = useState([{ color:'', size:'', country:'', qty:'', shipDate:'' }]);
    const [docs, setDocs] = useState({});   // { PO:{name,url}, TECHPACK:{...}, SPEC:{...} }
    const [bom, setBom] = useState([]);
    const [measurements, setMeasurements] = useState(null);
    const [busy, setBusy] = useState('');

    const setRow = (i,k,v) => setRows(a=>a.map((r,ix)=>ix===i?{...r,[k]:v}:r));

    // Same flow as StyleRegistry.handleSmartUpload
    const handleSmartUpload = async (e, type) => {
      const file = e.target.files[0]; if (!file) return;
      const base64 = await fileToBase64(file);
      setDocs(p=>({ ...p, [type]:{ name:file.name, url:base64, date:today() } }));
      e.target.value = '';
      if (!window.confirm(`Run AI Extraction on this ${type}?`)) return;
      setBusy(type); notify(`Extracting data from ${type} via AI...`, 'info');
      try {
        const data = await extractDocumentData(file, type);
        if (type === 'PO') {
          const newPOs = mapExtractedPOs(data, base64, file.name);
          setPendingCtx({ onConfirm: (accepted) => {
            const matrix = poLinesToMatrix(accepted);
            setRows(matrix.length ? matrix : rows);
            const first = accepted[0];
            setF(p=>({ ...p,
              poNo: p.poNo || first?.num || '',
              poQty: p.poQty || String(accepted.reduce((a,x)=>a+num(x.qty),0)),
              exFactory: p.exFactory || first?.date || '' }));
          }});
          setPendingPOs(newPOs);
          notify('Extraction complete — review and correct PO data before saving.', 'info');
        } else if (type === 'TECHPACK') {
          const mapped = mapExtractedBOM(data);
          setBom(prev => mergeBOM(prev, mapped));
          const sects = [...new Set(mapped.map(b=>b.category).filter(Boolean))].join(', ');
          notify(`BoM: added ${mapped.length} items${sects?` (${sects})`:''}. Upload the next page to keep adding.`, 'success');
        } else if (type === 'SPEC') {
          setMeasurements(mapExtractedSpec(data));
          notify('Graded measurements extracted!', 'success');
        }
      } catch (err) {
        notify(`Extraction failed: ${err.message}`, 'error');
      } finally { setBusy(''); }
    };

    const save = async () => {
      if (!f.styleNo || !f.supplier || !f.poNo || !f.poQty || !f.brand)
        return alert('Style No, Brand, Supplier, PO No and PO Qty are required');
      const matrix = rows.filter(r=>r.qty).map(r=>({ ...r, qty:num(r.qty) }));
      const style = { id:uid(), styleNo:f.styleNo, description:f.description, brand:f.brand,
        season:f.season, product:f.product, bom, measurements,
        docs:{ techpack:docs.TECHPACK?.name, poSheet:docs.PO?.name, specSheet:docs.SPEC?.name },
        createdOn:today(), createdBy:currentUser.name };
      const po = { id:uid(), poNo:f.poNo, styleNo:f.styleNo, supplier:f.supplier, brand:f.brand,
        poDate:f.poDate, qty:num(f.poQty), exFactory:f.exFactory, matrix, status:'Active' };

      const existingStyle = styles.find(s=>s.styleNo===f.styleNo);
      if (existingStyle) {
        const upd = { ...existingStyle, bom: mergeBOM(existingStyle.bom||[], bom),
          measurements: measurements || existingStyle.measurements };
        setDb(p=>({ ...p, styles: styles.map(s=>s.id===upd.id?upd:s) }));
        await saveToDB('style', upd);
      } else {
        setDb(p=>({ ...p, styles:[...styles, style] }));
        await saveToDB('style', style);
      }
      setDb(p=>({ ...p, pos:[...(p.pos||[]), po] }));
      await saveToDB('po', po);

      // Store uploaded documents against the PO so they appear in the gallery
      const docEntries = Object.entries(docs).map(([type,d]) => ({
        id: uid()+Math.random(), poNo:f.poNo, styleNo:f.styleNo,
        type: type==='PO' ? 'PO Sheet' : type==='TECHPACK' ? 'Techpack' : 'Spec Sheet',
        name:d.name, isImage:(d.url||'').startsWith('data:image'), data:d.url,
        uploadedBy:currentUser.name, date:today() }));
      if (docEntries.length) setDb(p=>({ ...p, poDocuments:[...(p.poDocuments||[]), ...docEntries] }));

      // Auto-generate the quality plan from the brand process
      const steps = (db.qualityProcess||[]).filter(q=>q.brand===f.brand).sort((a,b)=>a.seq-b.seq);
      if (steps.length) {
        const plan = { id:uid(), poNo:f.poNo, styleNo:f.styleNo, brand:f.brand, supplier:f.supplier,
          steps: steps.map(s=>({ seq:s.seq, name:s.name, mandatory:s.mandatory, format:s.format||'',
            scheduledDate: addDays(f.poDate, s.daysFromPO), status:'Not Started' })) };
        setDb(p=>({ ...p, qualityPlans:[...(p.qualityPlans||[]), plan] }));
        await saveToDB('qualityPlan', plan);
        notify(`Style & PO saved. Quality plan generated — ${steps.length} steps scheduled from ${f.poDate}.`, 'success');
      } else {
        notify(`Style & PO saved. No quality process defined for "${f.brand}" — add it in Admin → Quality Process.`, 'warn');
      }
      setF({ styleNo:'', description:'', brand:'', season:'', product:'', supplier:'', poNo:'',
        poDate:today(), poQty:'', exFactory:'' });
      setRows([{ color:'', size:'', country:'', qty:'', shipDate:'' }]);
      setDocs({}); setBom([]); setMeasurements(null);
    };

    const matrixTotal = rows.reduce((a,r)=>a+num(r.qty),0);
    const UPLOADS = [
      { type:'TECHPACK', label:'Techpack (BoM)', color:'#7c3aed', icon:'📐',
        hint:'Upload page by page — BoM items accumulate' },
      { type:'PO',       label:'PO Sheet',       color:'#0073ea', icon:'📋',
        hint:'Extracts PO no, colours, sizes, qty, destination, dates' },
      { type:'SPEC',     label:'Spec Sheet',     color:'#00a65e', icon:'📄',
        hint:'Extracts graded measurements (POMs)' },
    ];

    return (
      <div>
        <Section title="Upload Documents — AI Extraction (same engine as Nirvikalp StyleRegistry)">
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:14 }}>
            {UPLOADS.map(u=>(
              <div key={u.type} style={{ border:`1.5px dashed ${u.color}`, borderRadius:8, padding:14,
                background:`${u.color}08`, textAlign:'center' }}>
                <div style={{ fontSize:26 }}>{u.icon}</div>
                <div style={{ fontWeight:800, fontSize:12.5, color:u.color, margin:'4px 0' }}>{u.label}</div>
                <div style={{ fontSize:10.5, color:T.textSub, marginBottom:8 }}>{u.hint}</div>
                <label style={{ display:'inline-block', padding:'7px 14px', background:u.color, color:'white',
                  borderRadius:6, fontSize:11.5, fontWeight:700, cursor:'pointer' }}>
                  {busy===u.type ? 'Extracting…' : `Upload & AI Extract`}
                  <input type="file" accept="image/*,.csv,.txt" hidden onChange={e=>handleSmartUpload(e,u.type)} />
                </label>
                {docs[u.type] && <div style={{ fontSize:10.5, color:u.color, marginTop:6 }}>✓ {docs[u.type].name}</div>}
              </div>
            ))}
          </div>
          <div style={{ fontSize:10.5, color:T.textSub, marginTop:10, background:'#fffbeb',
            border:'1px solid #fde68a', borderRadius:6, padding:8 }}>
            For PDF or Excel documents, upload a screenshot (image) or save as CSV — same constraint as StyleRegistry.
            Extracted PO data always goes through a review screen before it is saved.
          </div>
        </Section>

        {bom.length>0 && (
          <Collapse title={`Extracted Techpack BoM — ${bom.length} items`} defaultOpen>
            <DynamicTable dense data={bom} setData={setBom}
              columns={[
                { label:'Category', key:'category' }, { label:'Item', key:'item_name' },
                { label:'Description', key:'description', width:'180px' }, { label:'Supplier', key:'supplier' },
                { label:'Reference', key:'reference' }, { label:'Consumption', key:'consumption' },
                { label:'Placement', key:'placement' }, { label:'Colour', key:'color_breakdown' },
                { label:'Size Variation', key:'size_variation' }, { label:'Comments', key:'comments' },
              ]}
              onAdd={()=>setBom([...bom,{ category:'General' }])} />
          </Collapse>
        )}
        {measurements && (
          <Collapse title={`Extracted Spec — ${measurements.rows?.length||0} measurement points`} defaultOpen>
            {measurements.rows?.length ? (
              <div style={{ overflowX:'auto' }}>
                <table style={{ borderCollapse:'collapse', fontSize:11.5, width:'100%' }}>
                  <thead><tr style={{ background:'#f1f5f9' }}>
                    <th style={{ padding:6, textAlign:'left' }}>POM</th>
                    <th style={{ padding:6 }}>Tol −</th><th style={{ padding:6 }}>Tol +</th>
                    {(measurements.sizes||[]).map(s=><th key={s} style={{ padding:6 }}>{s}</th>)}
                  </tr></thead>
                  <tbody>{measurements.rows.map((r,i)=>(
                    <tr key={i} style={{ borderBottom:'1px solid #f1f5f9' }}>
                      <td style={{ padding:6 }}>{r.pom||r.point||r.description||'—'}</td>
                      <td style={{ padding:6, textAlign:'center' }}>{r.tol_minus||r.tolMinus||'—'}</td>
                      <td style={{ padding:6, textAlign:'center' }}>{r.tol_plus||r.tolPlus||'—'}</td>
                      {(measurements.sizes||[]).map(s=><td key={s} style={{ padding:6, textAlign:'center' }}>{r[s]??'—'}</td>)}
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            ) : <pre style={{ fontSize:10.5, maxHeight:200, overflow:'auto' }}>{JSON.stringify(measurements,null,2)}</pre>}
          </Collapse>
        )}

        <Section title="Style & PO Information">
          <Grid cols={4}>
            <Field label="Style No *" value={f.styleNo} onChange={e=>setF({...f,styleNo:e.target.value})} />
            <Field label="Description" value={f.description} onChange={e=>setF({...f,description:e.target.value})} />
            <Field label="Brand *" value={f.brand} onChange={e=>setF({...f,brand:e.target.value})} options={brands} />
            <Field label="Season" value={f.season} onChange={e=>setF({...f,season:e.target.value})} placeholder="e.g. SS26" />
            <Field label="Product Type" value={f.product} onChange={e=>setF({...f,product:e.target.value})} />
            <Field label="Supplier *" value={f.supplier} onChange={e=>setF({...f,supplier:e.target.value})} options={suppliers} />
            <Field label="PO No *" value={f.poNo} onChange={e=>setF({...f,poNo:e.target.value})} />
            <Field label="PO Generation Date *" type="date" value={f.poDate} onChange={e=>setF({...f,poDate:e.target.value})} />
            <Field label="PO Qty *" type="number" value={f.poQty} onChange={e=>setF({...f,poQty:e.target.value})} />
            <Field label="Ex-Factory Date" type="date" value={f.exFactory} onChange={e=>setF({...f,exFactory:e.target.value})} />
          </Grid>
          {suppliers.length===0 && <div style={{ fontSize:11.5, color:T.warning }}>
            No approved suppliers yet — a supplier must be approved in Supplier → Dashboard before POs can be placed.
          </div>}
        </Section>

        <Section title={`Colour / Size / Country Matrix — total ${matrixTotal.toLocaleString()} ${f.poQty && matrixTotal!==num(f.poQty) ? `⚠ (PO qty ${num(f.poQty).toLocaleString()})` : matrixTotal?'✓':''}`}>
          <DynamicTable dense data={rows} setData={setRows}
            columns={[
              { label:'Colour', key:'color' }, { label:'Size', key:'size' },
              { label:'Country (shipment)', key:'country', type: countries.length?'select':'text', options:countries },
              { label:'Qty', key:'qty', type:'number' }, { label:'Shipment Date', key:'shipDate', type:'date' },
            ]}
            onAdd={()=>setRows([...rows,{ color:'', size:'', country:'', qty:'', shipDate:'' }])} />
          <Btn label="Save Style + PO + Generate Quality Plan" color={T.success} onClick={save} />
        </Section>
      </div>
    );
  };

  // ═══ TECHPACK & SPEC (per existing style) ═══
  const TechpackTab = () => {
    const [sel, setSel] = useState('');
    const [busy, setBusy] = useState('');
    const style = styles.find(s=>s.styleNo===sel);

    const upload = async (e, type) => {
      const file = e.target.files[0]; if (!file || !style) return;
      e.target.value='';
      if (!window.confirm(`Run AI Extraction on this ${type}?`)) return;
      setBusy(type); notify(`Extracting ${type} via AI...`, 'info');
      try {
        const data = await extractDocumentData(file, type);
        let upd = { ...style };
        if (type==='TECHPACK') {
          const mapped = mapExtractedBOM(data);
          upd.bom = mergeBOM(style.bom||[], mapped);
          notify(`BoM: added ${mapped.length} items. Total ${upd.bom.length}.`, 'success');
        } else if (type==='SPEC') {
          upd.measurements = mapExtractedSpec(data);
          notify('Graded measurements extracted!', 'success');
        }
        setDb(p=>({ ...p, styles: styles.map(s=>s.id===upd.id?upd:s) }));
        await saveToDB('style', upd);
      } catch (err) { notify(`Extraction failed: ${err.message}`, 'error'); }
      finally { setBusy(''); }
    };

    return (
      <div>
        <Section title="Techpack & Spec — add pages to an existing style">
          <Grid cols={3}>
            <Field label="Style" value={sel} onChange={e=>setSel(e.target.value)} options={styles.map(s=>s.styleNo)} />
            {style && <>
              <div style={{ display:'flex', alignItems:'flex-end', paddingBottom:10 }}>
                <label style={{ padding:'8px 14px', background:'#7c3aed', color:'white', borderRadius:6,
                  fontSize:11.5, fontWeight:700, cursor:'pointer' }}>
                  {busy==='TECHPACK'?'Extracting…':'📐 AI Extract Techpack'}
                  <input type="file" accept="image/*,.csv" hidden onChange={e=>upload(e,'TECHPACK')} />
                </label>
              </div>
              <div style={{ display:'flex', alignItems:'flex-end', paddingBottom:10 }}>
                <label style={{ padding:'8px 14px', background:'#00a65e', color:'white', borderRadius:6,
                  fontSize:11.5, fontWeight:700, cursor:'pointer' }}>
                  {busy==='SPEC'?'Extracting…':'📄 AI Extract Spec'}
                  <input type="file" accept="image/*,.csv" hidden onChange={e=>upload(e,'SPEC')} />
                </label>
              </div>
            </>}
          </Grid>
        </Section>
        {style && (
          <>
            {/* Upload buttons for existing style */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10, marginBottom:12 }}>
              {[
                { type:'PO',       label:'Upload / Update PO',    color:'#4f6ef7', icon:'📋' },
                { type:'TECHPACK', label:'Upload / Update Techpack', color:'#7c3aed', icon:'📐' },
                { type:'SPEC',     label:'Upload / Update Spec Sheet', color:'#06d6a0', icon:'📄' },
              ].map(u => (
                <label key={u.type} style={{ display:'flex', alignItems:'center', gap:7,
                  padding:'9px 14px', border:`1.5px solid ${u.color}`,
                  borderRadius:8, background:`${u.color}08`, cursor:'pointer',
                  fontSize:11.5, fontWeight:700, color:u.color }}>
                  <span>{u.icon}</span> {u.label}
                  <input type="file" accept=".pdf,.jpg,.jpeg,.png,.csv,.xlsx"
                    style={{ display:'none' }}
                    onChange={async e => {
                      const file = e.target.files[0]; if (!file) return;
                      notify(`Extracting ${u.type} data…`, 'info');
                      try {
                        const reader = new FileReader();
                        reader.onload = async ev => {
                          const res = await fetch(`${API_URL}/ai_service.php`, {
                            method:'POST', headers:{'Content-Type':'application/json'},
                            body:JSON.stringify({ file:ev.target.result,
                              fileType: file.type.includes('pdf') ? 'pdf' : 'image',
                              docType: u.type })
                          }).then(r=>r.json());
                          if (u.type === 'SPEC') {
                            const { mapExtractedSpec } = await import('./Extraction');
                            const mapped = mapExtractedSpec(res);
                            const upd = { ...style, measurements: mapped };
                            setDb(p=>({ ...p, styles:p.styles.map(s=>s.id===style.id?upd:s) }));
                            await saveToDB('style', upd);
                            notify(`Spec extracted — ${mapped.rows?.length||0} POMs`, 'success');
                          } else if (u.type === 'PO') {
                            notify('PO extracted — check Styles & POs tab to accept', 'success');
                          } else {
                            notify('Techpack extracted', 'success');
                          }
                        };
                        reader.readAsDataURL(file);
                      } catch(e) { notify('Extraction failed: '+e.message, 'error'); }
                      e.target.value = '';
                    }}/>
                </label>
              ))}
            </div>

            <Collapse title={`BoM — ${(style.bom||[]).length} items`} defaultOpen>
              <DataTable columns={['Category','Item','Description','Supplier','Reference','Consumption','Placement','Colour']}
                rows={(style.bom||[]).map((b,i)=>({ ...b, id:i }))}
                renderCell={(r,c)=>({ Category:r.category, Item:r.item_name, Description:r.description,
                  Supplier:r.supplier, Reference:r.reference, Consumption:r.consumption,
                  Placement:r.placement, Colour:r.color_breakdown }[c])}
                emptyMsg="No BoM yet — upload techpack above." />
            </Collapse>

            <Collapse title={`Measurements — ${style.measurements?.rows?.length||0} POMs`} defaultOpen>
              {style.measurements?.rows?.length ? (
                <div style={{ overflowX:'auto' }}>
                  <table style={{ borderCollapse:'collapse', fontSize:11.5, width:'100%' }}>
                    <thead><tr style={{ background:'linear-gradient(90deg,#1f1f1f,#2d3748)', color:'white' }}>
                      <th style={{ padding:'7px 10px', textAlign:'left', fontWeight:700 }}>Measurement Point</th>
                      <th style={{ padding:'7px 8px', textAlign:'center', fontWeight:700 }}>Tol −</th>
                      <th style={{ padding:'7px 8px', textAlign:'center', fontWeight:700 }}>Tol +</th>
                      {(style.measurements.sizes||[]).map(s=>(
                        <th key={s} style={{ padding:'7px 8px', textAlign:'center',
                          fontWeight:700, background:'rgba(79,110,247,0.3)' }}>{s}</th>
                      ))}
                    </tr></thead>
                    <tbody>{style.measurements.rows.map((r,i)=>(
                      <tr key={i} style={{ borderBottom:'1px solid #f1f5f9',
                        background:i%2===0?'white':'#fafbfc' }}>
                        <td style={{ padding:'6px 10px', fontWeight:600 }}>{r.pom||r.point||r.description||'—'}</td>
                        <td style={{ padding:'6px 8px', textAlign:'center', color:'#e2445c' }}>
                          {r.tol_minus||r.tolMinus||'—'}</td>
                        <td style={{ padding:'6px 8px', textAlign:'center', color:'#06d6a0' }}>
                          {r.tol_plus||r.tolPlus||'—'}</td>
                        {(style.measurements.sizes||[]).map(s=>(
                          <td key={s} style={{ padding:'6px 8px', textAlign:'center', fontWeight:600 }}>
                            {r[s]??'—'}
                          </td>
                        ))}
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              ) : (
                <div style={{ textAlign:'center', padding:20, color:T.textSub }}>
                  <div style={{ fontSize:11.5 }}>No spec extracted yet.</div>
                  <div style={{ fontSize:11, marginTop:4 }}>Use "Upload / Update Spec Sheet" above to extract measurements.</div>
                </div>
              )}
            </Collapse>
          </>
        )}
      </div>
    );
  };

  // ═══ STYLES & POs ═══
  const StyleList = () => (
    <div>
      <Grid cols={4} mb={14}>
        <StatCard label="Styles" value={styles.length} color={T.primary} />
        <StatCard label="Active POs" value={pos.filter(p=>p.status==='Active').length} color={T.teal} />
        <StatCard label="Total PO Qty" value={pos.reduce((a,p)=>a+num(p.qty),0).toLocaleString()} color={T.purple} />
        <StatCard label="Split POs" value={(db.poSplits||[]).length} color={T.warning} />
      </Grid>
      <Section title="Purchase Orders">
        <DataTable columns={['PO No','Style','Supplier','Brand','PO Date','Qty','Colours','Countries','Cut','Sew','Wash','Finish','Pack','Status']}
          rows={pos.slice().reverse()}
          renderCell={(r,c)=>{
            const st = k => { const v = stageQty(r.poNo,k);
              return <span style={{ color: v>=num(r.qty)?T.success:T.textSub, fontWeight:700 }}>{v.toLocaleString()}</span>; };
            return ({ 'PO No':<b>{r.poNo}</b>, Style:r.styleNo, Supplier:r.supplier, Brand:r.brand,
              'PO Date':r.poDate, Qty:num(r.qty).toLocaleString(),
              Colours:[...new Set((r.matrix||[]).map(m=>m.color).filter(Boolean))].join(', ')||'—',
              Countries:[...new Set((r.matrix||[]).map(m=>m.country).filter(Boolean))].join(', ')||'—',
              Cut:st('Cutting'), Sew:st('Sewing'), Wash:st('Washing'), Finish:st('Finishing'), Pack:st('Packing'),
              Status:<Badge status={r.status}/> })[c];
          }} />
      </Section>
    </div>
  );

  // ═══ QUALITY PLAN ═══
  const QualityPlan = () => {
    const plans = db.qualityPlans || [];
    const setStep = async (plan, seq, status) => {
      const upd = { ...plan, steps: plan.steps.map(s=>s.seq===seq
        ? { ...s, status, completedOn: status==='Passed'?today():s.completedOn } : s) };
      setDb(p=>({ ...p, qualityPlans: plans.map(x=>x.id===plan.id?upd:x) }));
      await saveToDB('qualityPlan', upd);
    };
    return (
      <div>
        {plans.slice().reverse().map(plan=>{
          const doneCount = plan.steps.filter(s=>['Passed','Approved'].includes(s.status)).length;
          return (
            <Collapse key={plan.id} defaultOpen
              title={`PO ${plan.poNo} · ${plan.styleNo} · ${plan.supplier} (${plan.brand} process)`}
              badge={`${doneCount}/${plan.steps.length} complete`}>
              <DataTable columns={['Seq','Process Step','Scheduled','Mandatory','Status','Set Status']}
                rows={plan.steps.map(s=>({ ...s, id:s.seq }))}
                renderCell={(r,c)=>({ Seq:<b>{r.seq}</b>, 'Process Step':r.name,
                  Scheduled:<span style={{ color: !['Passed','Approved'].includes(r.status) && r.scheduledDate<today() ? T.danger:'inherit', fontWeight:600 }}>{r.scheduledDate}</span>,
                  Mandatory: r.mandatory?'Yes':'No', Status:<Badge status={r.status}/>,
                  'Set Status':<select value={r.status} onChange={e=>setStep(plan,r.seq,e.target.value)}
                    style={{ fontSize:11, padding:3 }}>
                    {['Not Started','In Progress','Passed','Failed','Approved','Exception'].map(o=><option key={o}>{o}</option>)}
                  </select> }[c])} />
            </Collapse>
          );
        })}
        {plans.length===0 && <Section title="Quality Plans">
          <span style={{ fontSize:12, color:T.textSub }}>
            No plans yet — they generate automatically when a style + PO is created, based on the brand
            process defined in Admin → Quality Process.
          </span></Section>}
      </div>
    );
  };

  // ═══ DAILY PRODUCTION ═══
  const DailyProduction = () => {
    const [po, setPo]     = useState(null);
    const [lines, setLines] = useState([{ stage:'Packing', line:'', color:'', size:'', qty:'' }]);
    const [saving, setSaving] = useState(false);
    const [prodData, setProdData] = useState(null);
    const logs = db.productionLogs || [];

    const loadProd = async (poNo) => {
      if (!poNo) return;
      const res = await fetch(`${API_URL}/production_log.php`, {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ action:'get_summary', po_no:poNo })
      }).then(r=>r.json()).catch(()=>null);
      if (res?.success) setProdData(res);
    };

    const selectPO = (poNo) => {
      const p = pos.find(x=>x.poNo===poNo);
      setPo(p||null);
      setProdData(null);
      if (p) {
        loadProd(poNo);
        // Pre-fill lines from PO matrix
        const matrix = p.matrix||[];
        if (matrix.length) {
          setLines(matrix.map(m=>({ stage:'Packing', line:'', color:m.color||'', size:m.size||'', qty:'' })));
        }
      }
    };

    const addLine = () => setLines(p=>[...p,{ stage:'Packing', line:'', color:'', size:'', qty:'' }]);
    const updLine = (i,k,v) => setLines(p=>p.map((x,xi)=>xi===i?{...x,[k]:v}:x));

    const submit = async () => {
      if (!po) return alert('Select a PO first');
      const valid = lines.filter(l=>l.qty&&parseInt(l.qty)>0);
      if (!valid.length) return alert('Enter qty for at least one line');
      setSaving(true);
      try {
        const res = await fetch(`${API_URL}/production_log.php`, {
          method:'POST', headers:{'Content-Type':'application/json'},
          body: JSON.stringify({
            action: 'submit',
            po_no: po.poNo,
            style_no: po.styleNo,
            supplier: po.supplier,
            source: 'manual',
            submitted_by: currentUser.name,
            logs: valid.map(l=>({
              stage: l.stage, line: l.line,
              color: l.color, size: l.size,
              qty: parseInt(l.qty)||0,
            }))
          })
        }).then(r=>r.json());

        if (res.success) {
          notify(`${res.saved} line(s) saved. Server timestamp applied. Entries lock in 36 hours.`, 'success');
          await loadProd(po.poNo);
          // Also update local db for dashboard
          const newLogs = valid.map(l=>({ id:uid(), poNo:po.poNo, styleNo:po.styleNo,
            supplier:po.supplier, stage:l.stage, line:l.line, color:l.color,
            size:l.size, qty:parseInt(l.qty)||0, date:today(), enteredBy:currentUser.name }));
          setDb(p=>({ ...p, productionLogs:[...(p.productionLogs||[]), ...newLogs] }));
          newLogs.forEach(e=>saveToDB('productionLog',e));
          setLines([{ stage:'Packing', line:'', color:'', size:'', qty:'' }]);
        } else {
          alert(res.error||'Save failed');
        }
      } catch(e) { alert('Error: '+e.message); }
      setSaving(false);
    };

    return (
      <div>
        <Section title="Daily Production Entry">
          <div style={{ background:'#fff8e6', border:'1px solid #fde68a', borderRadius:6,
            padding:'8px 12px', marginBottom:12, fontSize:11.5, color:'#8a5a00' }}>
            🔒 <b>36-hour lock:</b> All entries are server-timestamped at submission time.
            You cannot set a past date. Entries become permanently locked after 36 hours.
            For final inspection cross-check, entries must be submitted within 36 hours of production.
          </div>
          <Grid cols={2}>
            <Field label="PO No *" value={po?.poNo||''} onChange={e=>selectPO(e.target.value)} options={pos.map(p=>p.poNo)} />
            {po && <div style={{ fontSize:11.5, padding:'8px 0', color:T.textSub }}>
              {po.styleNo} · {po.supplier} · {po.brand} · {num(po.qty).toLocaleString()} pcs
            </div>}
          </Grid>

          {po && (
            <>
              <div style={{ overflowX:'auto', marginBottom:10 }}>
                <table style={{ borderCollapse:'collapse', fontSize:11.5, width:'100%' }}>
                  <thead><tr style={{ background:'#f1f5f9' }}>
                    {['Stage','Line','Color','Size','Qty (pcs)',''].map(h=>(
                      <th key={h} style={{ padding:'6px 8px', textAlign:'left', fontWeight:800,
                        borderBottom:`1px solid ${T.border}` }}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>{lines.map((l,i)=>(
                    <tr key={i} style={{ borderBottom:`1px solid ${T.border}` }}>
                      <td style={{ padding:'4px 6px' }}>
                        <select value={l.stage} onChange={e=>updLine(i,'stage',e.target.value)}
                          style={{ padding:'5px', border:`1px solid ${T.border}`, borderRadius:4, fontSize:11 }}>
                          {STAGES.map(s=><option key={s}>{s}</option>)}
                        </select>
                      </td>
                      <td style={{ padding:'4px 6px' }}>
                        <input value={l.line} onChange={e=>updLine(i,'line',e.target.value)}
                          placeholder="L-01" style={{ width:55, padding:'5px', border:`1px solid ${T.border}`, borderRadius:4, fontSize:11 }} />
                      </td>
                      <td style={{ padding:'4px 6px' }}>
                        <select value={l.color} onChange={e=>updLine(i,'color',e.target.value)}
                          style={{ padding:'5px', border:`1px solid ${T.border}`, borderRadius:4, fontSize:11 }}>
                          <option value="">—</option>
                          {[...new Set((po.matrix||[]).map(m=>m.color).filter(Boolean))].map(c=><option key={c}>{c}</option>)}
                        </select>
                      </td>
                      <td style={{ padding:'4px 6px' }}>
                        <select value={l.size} onChange={e=>updLine(i,'size',e.target.value)}
                          style={{ padding:'5px', border:`1px solid ${T.border}`, borderRadius:4, fontSize:11 }}>
                          <option value="">—</option>
                          {[...new Set((po.matrix||[]).map(m=>m.size).filter(Boolean))].map(s=><option key={s}>{s}</option>)}
                        </select>
                      </td>
                      <td style={{ padding:'4px 6px' }}>
                        <input type="number" value={l.qty} onChange={e=>updLine(i,'qty',e.target.value)}
                          placeholder="0" style={{ width:75, padding:'5px', border:`1px solid ${T.border}`, borderRadius:4, fontSize:11, fontWeight:700 }} />
                      </td>
                      <td style={{ padding:'4px 6px' }}>
                        <button onClick={()=>setLines(p=>p.filter((_,xi)=>xi!==i))}
                          style={{ background:'none', border:'none', color:T.danger, cursor:'pointer', fontSize:15 }}>×</button>
                      </td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
              <div style={{ display:'flex', gap:10, alignItems:'center', flexWrap:'wrap' }}>
                <Btn small outline label="+ Add Line" onClick={addLine} color={T.primary} />
                <Btn label={saving?'Saving…':'Submit Production (server-timestamp)'}
                  color={T.success} disabled={saving} onClick={submit} />
                <span style={{ fontSize:11, color:T.textSub }}>
                  Entry date will be set to today ({today()}) by the server.
                </span>
              </div>
            </>
          )}
        </Section>

        {/* Live cross-check after submitting */}
        {po && prodData && (
          <Section title={`Production Summary — PO ${po.poNo}`}>
            <div style={{ overflowX:'auto' }}>
              <table style={{ borderCollapse:'collapse', fontSize:11.5, width:'100%' }}>
                <thead><tr style={{ background:'#1f1f1f', color:'white' }}>
                  {['Stage','Total','Locked','Editable (36hr)','Balance','%'].map(h=>(
                    <th key={h} style={{ padding:'7px 10px', textAlign:'left', fontWeight:700 }}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>{(prodData.stages||[]).map(s=>{
                  const pct=num(po.qty)?Math.round(num(s.qty)/num(po.qty)*100):0;
                  const col=pct>=100?T.success:pct>=80?T.warning:T.danger;
                  return (
                    <tr key={s.stage} style={{ borderBottom:`1px solid ${T.border}` }}>
                      <td style={{ padding:'7px 10px', fontWeight:700 }}>{s.stage}</td>
                      <td style={{ padding:'7px 10px', fontWeight:900, color:col }}>{num(s.qty).toLocaleString()}</td>
                      <td style={{ padding:'7px 10px' }}><Tag label={`${num(s.qty_locked).toLocaleString()}`} color={T.success}/></td>
                      <td style={{ padding:'7px 10px' }}>
                        {num(s.qty_editable)>0?<Tag label={num(s.qty_editable).toLocaleString()} color={T.warning}/>:'—'}
                      </td>
                      <td style={{ padding:'7px 10px', color:T.textSub }}>{(num(po.qty)-num(s.qty)).toLocaleString()}</td>
                      <td style={{ padding:'7px 10px', fontWeight:800, color:col }}>{pct}%</td>
                    </tr>
                  );
                })}</tbody>
              </table>
            </div>
          </Section>
        )}

        {/* Legacy logs for reference */}
        {logs.length > 0 && (
          <Section title="Recent Entries (legacy)">
            <div style={{ fontSize:11, color:T.textSub, marginBottom:8 }}>
              These are entries made before the 36-hour lock system was introduced.
            </div>
            <DataTable columns={['Date','PO','Stage','Color','Size','Qty','By']}
              rows={logs.slice(-10).reverse()}
              renderCell={(r,c)=>({ Date:r.date, PO:r.poNo, Stage:r.stage,
                Color:r.color||'—', Size:r.size||'—', Qty:num(r.qty).toLocaleString(), By:r.enteredBy }[c])} />
          </Section>
        )}
      </div>
    );
  };

  // ═══ INSPECTION OFFER ═══
  const InspectionOffer = () => {
    const [f, setF] = useState({ poNo:'', stage:'Inline', date:today() });
    const [splitF, setSplitF] = useState({ poNo:'', qty:'' });
    const exceptions = db.exceptions || [];
    const po = pos.find(p=>p.poNo===f.poNo);
    const readyStage = { 'Inline':'Sewing', 'Pre-Final':'Finishing', 'Final':'Packing' }[f.stage];
    const ready = po ? stageQty(po.poNo, readyStage) : 0;
    const required = po ? Math.ceil(num(po.qty) * CUTOFF_PCT[f.stage] / 100) : 0;
    const ok = ready >= required;
    const approvedExc = exceptions.find(e=>e.poNo===f.poNo && e.stage===f.stage &&
      e.type==='Cut-off Exception' && e.status==='Approved');

    const offer = async () => {
      if (!po) return alert('Select a PO');
      if (!ok && !approvedExc) return alert(
        `Cut-off qty not ready: ${f.stage} inspection requires ${required.toLocaleString()} pcs at ${readyStage} ` +
        `(${CUTOFF_PCT[f.stage]}% of PO). Ready: ${ready.toLocaleString()}. Request a manager exception below.`);
      const entry = { id:uid(), poNo:po.poNo, styleNo:po.styleNo, supplier:po.supplier, brand:po.brand,
        stage:f.stage, date:f.date, qaMember:(db.qaAllocations||[]).find(a=>a.supplier===po.supplier)?.qaMember||'',
        status:'Scheduled', result:'Pending', offeredBy:currentUser.name, viaException: !ok && !!approvedExc };
      setDb(p=>({ ...p, inspections:[...(p.inspections||[]), entry] }));
      await saveToDB('inspection', entry);
      notify(`${f.stage} inspection offered for PO ${po.poNo}${entry.viaException?' (via approved exception)':''}.`, 'success');
    };
    const requestException = async () => {
      if (!po) return;
      const entry = { id:uid(), type:'Cut-off Exception', poNo:po.poNo, supplier:po.supplier, stage:f.stage,
        detail:`Ready ${ready}/${required} at ${readyStage}`, raisedBy:currentUser.name, raisedOn:today(),
        status:'Pending Approval', kpi:'Exceptions Raised' };
      setDb(p=>({ ...p, exceptions:[...exceptions, entry] }));
      await saveToDB('exception', entry);
      notify('Exception request sent to QA Manager.', 'info');
    };
    const decide = async (exc, status) => {
      if (!isManager(currentUser)) return alert('Only a QA Manager can approve exceptions.');
      const upd = { ...exc, status, decidedBy:currentUser.name, decidedOn:today() };
      setDb(p=>({ ...p, exceptions: exceptions.map(x=>x.id===exc.id?upd:x) }));
      await saveToDB('exception', upd);
    };
    const split = async () => {
      const parent = pos.find(p=>p.poNo===splitF.poNo);
      if (!parent || !num(splitF.qty)) return alert('Select PO and split qty');
      if (!exceptions.some(e=>e.poNo===parent.poNo && e.status==='Approved'))
        return alert('PO split for inspection is allowed only when an exception has been approved by the QA Manager.');
      if (num(splitF.qty) >= num(parent.qty)) return alert('Split qty must be less than PO qty');
      const childNo = `${parent.poNo}-S${((db.poSplits||[]).filter(s=>s.parentPO===parent.poNo).length)+1}`;
      const splitEntry = { id:uid(), parentPO:parent.poNo, childPO:childNo, qty:num(splitF.qty), date:today(), by:currentUser.name };
      const childPO = { ...parent, id:uid(), poNo:childNo, qty:num(splitF.qty), status:'Split', matrix:[] };
      setDb(p=>({ ...p, poSplits:[...(p.poSplits||[]), splitEntry],
        pos:[...p.pos.map(x=>x.id===parent.id?{...x,qty:num(parent.qty)-num(splitF.qty)}:x), childPO] }));
      await saveToDB('poSplit', splitEntry); await saveToDB('po', childPO);
      notify(`Split PO ${childNo} created (${num(splitF.qty).toLocaleString()} pcs).`, 'success');
      setSplitF({ poNo:'', qty:'' });
    };

    return (
      <div>
        <Section title="Offer Inspection — cut-off qty gate">
          <Grid cols={4}>
            <Field label="PO" value={f.poNo} onChange={e=>setF({...f,poNo:e.target.value})} options={pos.map(p=>p.poNo)} />
            <Field label="Inspection Stage" value={f.stage} onChange={e=>setF({...f,stage:e.target.value})} options={Object.keys(CUTOFF_PCT)} />
            <Field label="Requested Date" type="date" value={f.date} onChange={e=>setF({...f,date:e.target.value})} />
            <div style={{ display:'flex', alignItems:'flex-end', paddingBottom:10 }}>
              <Btn label="Offer Inspection" color={ok||approvedExc?T.success:T.warning} onClick={offer} />
            </div>
          </Grid>
          {po && (
            <div style={{ fontSize:12.5, padding:10, borderRadius:8,
              background: ok?'#00c87515':'#e2445c12', border:`1px solid ${ok?T.success:T.danger}` }}>
              Cut-off rule: <b>{f.stage}</b> requires <b>{CUTOFF_PCT[f.stage]}%</b> of PO qty ready at <b>{readyStage}</b> →
              required <b>{required.toLocaleString()}</b>, ready <b>{ready.toLocaleString()}</b> — {ok
                ? <b style={{ color:T.success }}>OK to offer ✓</b>
                : approvedExc
                  ? <b style={{ color:T.warning }}>Blocked, but manager exception approved — offer permitted ⚠</b>
                  : <span><b style={{ color:T.danger }}>BLOCKED ✕</b>{' '}
                      <Btn small color={T.warning} label="Request Manager Exception" onClick={requestException} /></span>}
            </div>
          )}
        </Section>
        <Section title="Exception Requests (QA Manager approval)">
          <DataTable columns={['Raised','PO','Supplier','Stage','Detail','By','Status']}
            rows={exceptions.filter(e=>e.type==='Cut-off Exception').slice().reverse()}
            renderCell={(r,c)=>({ Raised:r.raisedOn, PO:r.poNo, Supplier:r.supplier, Stage:r.stage,
              Detail:r.detail, By:r.raisedBy, Status:<Badge status={r.status}/> }[c])}
            actions={r => r.status==='Pending Approval' && <span style={{ display:'flex', gap:5 }}>
              <Btn small color={T.success} label="Approve" onClick={()=>decide(r,'Approved')} />
              <Btn small color={T.danger} label="Reject" onClick={()=>decide(r,'Rejected')} /></span>} />
        </Section>
        <Section title="Split PO Qty for Inspection (only when an exception is approved)">
          <Grid cols={3}>
            <Field label="Parent PO" value={splitF.poNo} onChange={e=>setSplitF({...splitF,poNo:e.target.value})}
              options={pos.filter(p=>p.status==='Active').map(p=>p.poNo)} />
            <Field label="Split Qty" type="number" value={splitF.qty} onChange={e=>setSplitF({...splitF,qty:e.target.value})} />
            <div style={{ display:'flex', alignItems:'flex-end', paddingBottom:10 }}>
              <Btn label="Create Split PO" color={T.purple} onClick={split} /></div>
          </Grid>
          <DataTable columns={['Date','Parent PO','Child PO','Qty','By']} rows={(db.poSplits||[]).slice().reverse()}
            renderCell={(r,c)=>({ Date:r.date, 'Parent PO':r.parentPO, 'Child PO':<b>{r.childPO}</b>,
              Qty:r.qty.toLocaleString(), By:r.by }[c])} />
        </Section>
      </div>
    );
  };

  // ═══ SUPPLIER LINKS (packing list · 48-hr production · PPM) ═══
  const SupplierLinks = () => {
    const [f, setF] = useState({ poNo:'', type:'Packing List & Invoice Upload' });
    const links = db.supplierLinks || [];
    const LINK_TYPES = {
      'Packing List & Invoice Upload': { path:'packing',    hours:null, note:'Supplier must upload packing list & invoice before inspection.' },
      'Daily Production Entry':        { path:'production', hours:48,   note:'Valid for 48 hours only.' },
      'Pre-Production Meeting (PPM)':  { path:'ppm',        hours:168,  note:'Supplier fills the PPM form (J-01); your QA reviews and approves it. Valid 7 days.' },
    };
    const gen = async () => {
      const po = pos.find(p=>p.poNo===f.poNo);
      if (!po) return alert('Select a PO');
      const cfg = LINK_TYPES[f.type];
      const token = Math.random().toString(36).slice(2,10).toUpperCase();
      const entry = { id:uid(), poNo:po.poNo, styleNo:po.styleNo, supplier:po.supplier, type:f.type, token,
        url:`${API_URL.replace('/api','')}/portal/?type=${cfg.path}&token=${token}`,
        createdOn:today(), createdBy:currentUser.name, createdAt:Date.now(),
        expiresAt: cfg.hours ? Date.now() + cfg.hours*3600*1000 : null,
        status:'Active', submitted:false };
      setDb(p=>({ ...p, supplierLinks:[...links, entry] }));
      await saveToDB('supplierLink', entry);
      navigator.clipboard?.writeText(entry.url).catch(()=>{});
      alert(`Link generated & copied:\n${entry.url}\n\n${cfg.note}\nSend it to ${po.supplier}.`);
    };
    const isExpired = l => l.expiresAt && Date.now() > l.expiresAt;
    return (
      <Section title="Generate Supplier Links">
        <Grid cols={3}>
          <Field label="PO" value={f.poNo} onChange={e=>setF({...f,poNo:e.target.value})} options={pos.map(p=>p.poNo)} />
          <Field label="Link Type" value={f.type} onChange={e=>setF({...f,type:e.target.value})} options={Object.keys(LINK_TYPES)} />
          <div style={{ display:'flex', alignItems:'flex-end', paddingBottom:10 }}>
            <Btn label="Generate & Copy Link" onClick={gen} /></div>
        </Grid>
        <div style={{ fontSize:11.5, color:T.textSub, background:'#f8fafc', padding:'8px 10px',
          borderRadius:6, marginBottom:10 }}>
          {LINK_TYPES[f.type].note}
        </div>
        <DataTable columns={['Created','PO','Supplier','Type','Link','Validity','Submitted']} rows={links.slice().reverse()}
          renderCell={(r,c)=>({ Created:r.createdOn, PO:r.poNo, Supplier:r.supplier, Type:r.type,
            Link:<code style={{ fontSize:10.5 }}>{r.url}</code>,
            Validity: r.expiresAt ? (isExpired(r) ? <Badge status="Overdue"/> : <Badge status="Active"/>) : <Badge status="Active"/>,
            Submitted: r.submitted ? <Badge status="Completed"/> : <Badge status="Pending"/> }[c])} />
      </Section>
    );
  };

  // ═══ DOCUMENTS ═══
  const Documents = () => {
    const [f, setF] = useState({ poNo:'', type:'Packing List' });
    const docs = db.poDocuments || [];
    const upload = (e) => {
      const files = Array.from(e.target.files||[]);
      if (!f.poNo) { alert('Select a PO first'); e.target.value=''; return; }
      files.forEach(file => {
        const r = new FileReader();
        r.onload = async () => {
          const entry = { id:uid()+Math.random(), poNo:f.poNo, type:f.type, name:file.name,
            isImage:file.type.startsWith('image'), data:r.result, uploadedBy:currentUser.name, date:today() };
          setDb(p=>({ ...p, poDocuments:[...(p.poDocuments||[]), entry] }));
          await saveToDB('poDocument', { ...entry, data:'(stored)' });
        };
        r.readAsDataURL(file);
      });
      e.target.value='';
    };
    const shown = docs.filter(d=>!f.poNo || d.poNo===f.poNo);
    const printGallery = () => {
      const imgs = shown.filter(d=>d.isImage);
      printHTML(`PO ${f.poNo||'All'} — Document Gallery`, `
        <h1>TOQUM · PO ${f.poNo||'All'} — Document Gallery</h1>
        <div class="imgs">${imgs.map(d=>`<div><img src="${d.data}"/>
          <div style="font-size:8px">${d.type} · ${d.name} · ${d.date}</div></div>`).join('')}</div>
        <h3>Document Index</h3>
        <table><tr><th>Type</th><th>Name</th><th>PO</th><th>Uploaded By</th><th>Date</th></tr>
          ${shown.map(d=>`<tr><td>${d.type}</td><td>${d.name}</td><td>${d.poNo}</td>
            <td>${d.uploadedBy}</td><td>${d.date}</td></tr>`).join('')}</table>`);
    };
    return (
      <Section title="PO Documents — techpack, PO sheet, spec, packing lists, inspection reports, images"
        actions={<Btn small outline label="🖨 Print Gallery" onClick={printGallery} />}>
        <Grid cols={3}>
          <Field label="PO" value={f.poNo} onChange={e=>setF({...f,poNo:e.target.value})} options={pos.map(p=>p.poNo)} />
          <Field label="Document Type" value={f.type} onChange={e=>setF({...f,type:e.target.value})}
            options={['Packing List','Invoice','Inspection Report','Techpack','PO Sheet','Spec Sheet','Image','Test Report','Other']} />
          <div style={{ display:'flex', alignItems:'flex-end', paddingBottom:10 }}>
            <label style={{ fontSize:12, fontWeight:700, color:'white', background:T.primary,
              padding:'8px 14px', borderRadius:5, cursor:'pointer' }}>
              📤 Upload<input type="file" multiple onChange={upload} style={{ display:'none' }} /></label>
          </div>
        </Grid>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))', gap:12 }}>
          {shown.map(d=>(
            <div key={d.id} style={{ border:`1px solid ${T.border}`, borderRadius:8, overflow:'hidden', background:'white' }}>
              {d.isImage
                ? <img src={d.data} alt={d.name} style={{ width:'100%', height:110, objectFit:'cover' }} />
                : <div style={{ height:110, display:'flex', alignItems:'center', justifyContent:'center',
                    fontSize:34, background:'#f8fafc' }}>📄</div>}
              <div style={{ padding:8, fontSize:10.5 }}>
                <b>{d.type}</b> · PO {d.poNo}<br/>
                <span style={{ color:T.textSub }}>{d.name}</span><br/>
                <span style={{ color:T.textSub }}>{d.date} · {d.uploadedBy}</span>
              </div>
            </div>
          ))}
          {shown.length===0 && <span style={{ fontSize:12, color:T.textSub }}>No documents yet.</span>}
        </div>
      </Section>
    );
  };

  // ═══ ALLOCATION — style → supplier → QA ═══
  const Allocation = () => {
    const allocs = db.styleAllocations || [];
    const [f, setF] = useState({ styleNo:'', supplier:'', qaMember:'', poNo:'', note:'' });
    const qaUsers = (db.users||[]).filter(u=>['QA','Manager','User','Admin'].includes(u.role)).map(u=>u.name);
    const allSuppliers = (db.suppliers||[]).map(s=>s.name);

    // Styles that arrived without a supplier attached from merchandising
    const unallocated = styles.filter(st =>
      !pos.some(p=>p.styleNo===st.styleNo) && !allocs.some(a=>a.styleNo===st.styleNo));

    const add = async () => {
      if (!isManager(currentUser)) return notify('Allocation is done by a manager with admin rights.', 'error');
      if (!f.styleNo || !f.supplier) return notify('Style and supplier are required', 'error');
      const entry = { id:uid(), ...f, allocatedBy:currentUser.name, date:today(), status:'Active' };
      setDb(p=>({ ...p, styleAllocations:[...allocs, entry] }));
      await saveToDB('styleAllocation', entry);

      // Allocating a style to a supplier also creates the QA link used everywhere else
      if (f.qaMember && !(db.qaAllocations||[]).some(a=>a.supplier===f.supplier && a.qaMember===f.qaMember)) {
        const qa = { id:uid(), supplier:f.supplier, qaMember:f.qaMember,
          allocatedBy:currentUser.name, date:today(), source:'Style allocation' };
        setDb(p=>({ ...p, qaAllocations:[...(p.qaAllocations||[]), qa] }));
        await saveToDB('qaAllocation', qa);
      }
      notify(`Style ${f.styleNo} allocated to ${f.supplier}${f.qaMember?` · QA ${f.qaMember}`:''}.`, 'success');
      setF({ styleNo:'', supplier:'', qaMember:'', poNo:'', note:'' });
    };

    const remove = (a) => {
      if (!isManager(currentUser)) return notify('Manager rights required.', 'error');
      setDb(p=>({ ...p, styleAllocations: allocs.filter(x=>x.id!==a.id) }));
    };

    return (
      <div>
        <CardRow min={200}>
          <StatCard label="Styles" value={styles.length} color={T.primary} />
          <StatCard label="Allocated" value={allocs.length} color={T.success} />
          <StatCard label="Unallocated Styles" value={unallocated.length}
            color={unallocated.length?T.warning:T.success} />
          <StatCard label="Supplier↔QA Links" value={(db.qaAllocations||[]).length} color={T.teal} />
        </CardRow>

        <Section title={`Allocate Style → Supplier → QA ${isManager(currentUser)?'':'(manager rights required)'}`}>
          <Grid cols={5}>
            <Field label="Style *" value={f.styleNo} onChange={e=>setF({...f,styleNo:e.target.value})}
              options={styles.map(s=>s.styleNo)} disabled={!isManager(currentUser)} />
            <Field label="Supplier *" value={f.supplier} onChange={e=>setF({...f,supplier:e.target.value})}
              options={allSuppliers} disabled={!isManager(currentUser)} />
            <Field label="QA Member" value={f.qaMember} onChange={e=>setF({...f,qaMember:e.target.value})}
              options={qaUsers} disabled={!isManager(currentUser)} />
            <Field label="PO (optional)" value={f.poNo} onChange={e=>setF({...f,poNo:e.target.value})}
              options={pos.map(p=>p.poNo)} disabled={!isManager(currentUser)} />
            <div style={{ display:'flex', alignItems:'flex-end', paddingBottom:10 }}>
              <Btn label="Allocate" onClick={add} disabled={!isManager(currentUser)} />
            </div>
          </Grid>
          <div style={{ fontSize:11.5, color:T.textSub, background:'#f8fafc', padding:'8px 10px', borderRadius:6 }}>
            Use this when merchandising has not attached a supplier to the style. The same allocation
            creates the supplier ↔ QA link, so the QA immediately sees this style and its POs in their
            own module — no separate step in Admin needed.
          </div>
        </Section>

        {unallocated.length>0 && (
          <Section title="⚠ Styles with no supplier and no allocation">
            <DataTable columns={['Style','Description','Brand','Season','Created']} rows={unallocated}
              renderCell={(r,c)=>({ Style:<b>{r.styleNo}</b>, Description:r.description,
                Brand:r.brand, Season:r.season, Created:r.createdOn }[c])}
              actions={r => <Btn small label="Allocate" onClick={()=>setF({...f,styleNo:r.styleNo})} />} />
          </Section>
        )}

        <Section title="Style Allocation Register">
          <DataTable columns={['Date','Style','Supplier','QA Member','PO','Allocated By','Status']} rows={allocs.slice().reverse()}
            renderCell={(r,c)=>({ Date:r.date, Style:<b>{r.styleNo}</b>, Supplier:r.supplier,
              'QA Member': r.qaMember ? <Tag label={r.qaMember} color={T.teal}/> : <Tag label="not set" color={T.warning}/>,
              PO:r.poNo||'—', 'Allocated By':r.allocatedBy, Status:<Badge status={r.status}/> }[c])}
            actions={r => isManager(currentUser) && <Btn small color={T.danger} label="✕" onClick={()=>remove(r)} />}
            emptyMsg="No style allocations yet." />
        </Section>
      </div>
    );
  };

  // ═══ PRODUCTION MONITOR — milestones + quality vs production ═══
  const ProductionMonitor = () => {
    const [sel, setSel] = useState('');
    const po = pos.find(p=>p.poNo===sel);
    const logs = (db.productionLogs||[]).filter(l=>l.poNo===sel);
    const insp = (db.inspections||[]).filter(i=>i.poNo===sel);
    const plan = (db.qualityPlans||[]).find(q=>q.poNo===sel);

    // Milestone ribbon: each production stage + the quality gate that follows it
    const milestones = useMemo(() => {
      if (!po) return [];
      const qty = num(po.qty);
      const gateFor = { Cutting:'Cutting Inline', Sewing:'Sewing Inline',
        Washing:null, Finishing:'Finishing Inline', Packing:'Final' };
      return STAGES.map(st => {
        const done = logs.filter(l=>l.stage===st).reduce((a,l)=>a+num(l.qty),0);
        const pct = qty ? Math.round(done/qty*100) : 0;
        const gateName = gateFor[st];
        const gateInsp = gateName ? insp.filter(i=>(i.stage||'').includes(gateName.split(' ')[0])) : [];
        const passed = gateInsp.filter(i=>i.result==='Passed').length;
        const failed = gateInsp.filter(i=>i.result==='Failed').length;
        const defects = gateInsp.reduce((a,i)=>a+num(i.totals?.critical)+num(i.totals?.major)+num(i.totals?.minor),0);
        return { stage:st, done, pct, gateName, inspections:gateInsp.length, passed, failed, defects,
          state: pct>=100 ? (failed ? 'issue' : passed ? 'clear' : 'done')
               : pct>0 ? 'running' : 'pending' };
      });
    }, [po, logs, insp]);

    // Daily quality vs production series for this PO
    const series = useMemo(() => {
      const map = {};
      logs.forEach(l => {
        const d = l.date;
        map[d] = map[d] || { date:d, produced:0, defects:0, checked:0, inspections:0 };
        map[d].produced += num(l.qty);
      });
      insp.forEach(i => {
        const d = i.date;
        map[d] = map[d] || { date:d, produced:0, defects:0, checked:0, inspections:0 };
        map[d].inspections++;
        map[d].checked += num(i.header?.visualCheckQty);
        map[d].defects += num(i.totals?.critical)+num(i.totals?.major)+num(i.totals?.minor);
      });
      let cum = 0;
      return Object.values(map).sort((a,b)=>a.date.localeCompare(b.date)).map(d => {
        cum += d.produced;
        return { ...d, cumulative:cum, label:d.date.slice(5),
          dhu: d.checked ? +((d.defects/d.checked)*100).toFixed(2) : null };
      });
    }, [logs, insp]);

    // Output is the final stage only — summing all stages counts each piece
    // once per stage it passes through.
    const totalProduced = logs.filter(l=>l.stage==='Packing').reduce((a,l)=>a+num(l.qty),0);
    const totalDefects  = insp.reduce((a,i)=>a+num(i.totals?.critical)+num(i.totals?.major)+num(i.totals?.minor),0);
    const totalChecked  = insp.reduce((a,i)=>a+num(i.header?.visualCheckQty),0);
    // DHU = defects per hundred units — a rate, not a percentage.
    const overallDhu    = totalChecked ? (totalDefects/totalChecked*100).toFixed(2) : '—';
    const passRate = insp.filter(i=>['Passed','Failed'].includes(i.result)).length
      ? Math.round(insp.filter(i=>i.result==='Passed').length /
          insp.filter(i=>['Passed','Failed'].includes(i.result)).length * 100) : null;

    const STATE_STYLE = {
      clear:   { bg:'#00c875', label:'Quality cleared', icon:'✓' },
      done:    { bg:'#0073ea', label:'Complete, not inspected', icon:'●' },
      issue:   { bg:'#e2445c', label:'Failed inspection', icon:'!' },
      running: { bg:'#fdab3d', label:'In progress', icon:'▶' },
      pending: { bg:'#c3c9d6', label:'Not started', icon:'○' },
    };

    return (
      <div>
        <Section title="Select a PO to monitor">
          <Grid cols={4}>
            <Field label="PO" value={sel} onChange={e=>setSel(e.target.value)} options={pos.map(p=>p.poNo)} />
          </Grid>
        </Section>

        {po && (
          <>
            <CardRow min={190}>
              <StatCard label="PO Qty" value={num(po.qty).toLocaleString()} color={T.primary} />
              <StatCard label="Produced (packing)" value={milestones.find(m=>m.stage==='Packing')?.done.toLocaleString()||'0'}
                sub={`${milestones.find(m=>m.stage==='Packing')?.pct||0}% of PO`} color={T.teal} />
              <StatCard label="Inspections" value={insp.length}
                sub={passRate !== null ? `${passRate}% pass` : 'none completed'} color={T.success} />
              <StatCard label="Overall DHU" value={overallDhu}
                sub={`${totalDefects} defects per ${totalChecked.toLocaleString()} units checked`} color={T.warning} />
              <StatCard label="Plan Progress"
                value={plan ? `${plan.steps.filter(s=>['Passed','Approved'].includes(s.status)).length}/${plan.steps.length}` : '—'}
                color={T.purple} />
              <StatCard label="Days to Ex-Factory"
                value={po.exFactory ? Math.ceil((new Date(po.exFactory)-new Date())/86400000) : '—'}
                sub={po.exFactory||''} color={T.orange} />
            </CardRow>

            <Section title="Quality Milestones — production stage and the gate that follows it">
              <div style={{ display:'flex', gap:0, alignItems:'stretch', flexWrap:'wrap' }}>
                {milestones.map((m,i)=>{
                  const st = STATE_STYLE[m.state];
                  return (
                    <React.Fragment key={m.stage}>
                      <div style={{ flex:'1 1 150px', minWidth:150, background:'white',
                        border:`1px solid ${T.border}`, borderTop:`4px solid ${st.bg}`,
                        borderRadius:10, padding:'12px 14px', boxShadow:T.cardShadow }}>
                        <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6 }}>
                          <span style={{ width:24, height:24, borderRadius:'50%', background:st.bg,
                            color:'white', display:'flex', alignItems:'center', justifyContent:'center',
                            fontWeight:900, fontSize:12 }}>{st.icon}</span>
                          <b style={{ fontSize:12.5 }}>{m.stage}</b>
                        </div>
                        <div style={{ fontSize:19, fontWeight:900, lineHeight:1 }}>{m.pct}%</div>
                        <div style={{ fontSize:10.5, color:T.textSub, marginBottom:6 }}>
                          {m.done.toLocaleString()} / {num(po.qty).toLocaleString()} pcs</div>
                        <div style={{ height:5, background:'#f1f5f9', borderRadius:3, marginBottom:7 }}>
                          <div style={{ height:5, width:`${Math.min(100,m.pct)}%`, background:st.bg, borderRadius:3 }} />
                        </div>
                        <div style={{ fontSize:10, color:T.textSub, fontWeight:700 }}>{st.label}</div>
                        {m.gateName && (
                          <div style={{ marginTop:6, paddingTop:6, borderTop:`1px dashed ${T.border}`, fontSize:10 }}>
                            <div style={{ color:T.textSub, fontWeight:700 }}>{m.gateName} gate</div>
                            {m.inspections ? (
                              <div>
                                <span style={{ color:T.success, fontWeight:800 }}>{m.passed} pass</span>
                                {m.failed>0 && <span style={{ color:T.danger, fontWeight:800 }}> · {m.failed} fail</span>}
                                {m.defects>0 && <span style={{ color:T.warning }}> · {m.defects} defects</span>}
                              </div>
                            ) : <span style={{ color:T.textSub }}>not inspected yet</span>}
                          </div>
                        )}
                      </div>
                      {i < milestones.length-1 && (
                        <div style={{ display:'flex', alignItems:'center', padding:'0 4px',
                          color: m.pct>=100 ? T.success : T.border, fontSize:18, fontWeight:900 }}>›</div>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            </Section>

            <Split min={430}>
              <Section title="Cumulative Output vs PO Quantity">
                <ResponsiveContainer width="100%" height={260}>
                  <ComposedChart data={series}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eef1f6" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize:10, fill:T.textSub }} tickLine={false} />
                    <YAxis tick={{ fontSize:10, fill:T.textSub }} tickLine={false} axisLine={false} />
                    <Tooltip contentStyle={{ fontSize:11.5, borderRadius:8 }} />
                    <Legend wrapperStyle={{ fontSize:11 }} />
                    <Area type="monotone" dataKey="cumulative" name="Cumulative output"
                      stroke={T.primary} fill={T.primary} fillOpacity={0.15} strokeWidth={2} />
                    <Bar dataKey="produced" name="Daily output (all stages)" fill={T.teal} radius={[3,3,0,0]} />
                  </ComposedChart>
                </ResponsiveContainer>
              </Section>
              <Section title="Quality Performance vs Production Volume">
                <ResponsiveContainer width="100%" height={260}>
                  <ComposedChart data={series}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eef1f6" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize:10, fill:T.textSub }} tickLine={false} />
                    <YAxis yAxisId="l" tick={{ fontSize:10, fill:T.textSub }} tickLine={false} axisLine={false} />
                    <YAxis yAxisId="r" orientation="right" tick={{ fontSize:10, fill:T.textSub }}
                      tickLine={false} axisLine={false} />
                    <Tooltip contentStyle={{ fontSize:11.5, borderRadius:8 }} />
                    <Legend wrapperStyle={{ fontSize:11 }} />
                    <Bar yAxisId="l" dataKey="produced" name="Produced" fill={T.primary} radius={[3,3,0,0]} />
                    <Bar yAxisId="l" dataKey="checked" name="Units inspected" fill={T.teal} radius={[3,3,0,0]} />
                    <Line yAxisId="r" type="monotone" dataKey="dhu" name="DHU (per 100 units)" stroke={T.danger}
                      strokeWidth={2.5} dot={{ r:3 }} connectNulls />
                  </ComposedChart>
                </ResponsiveContainer>
              </Section>
            </Split>

            <Section title="Inspection Events Against This PO">
              <DataTable columns={['Date','Stage','QA','Checked','Cri/Maj/Min','Result','Via Exception']}
                rows={insp.slice().reverse()}
                renderCell={(r,c)=>({ Date:r.date, Stage:r.stage, QA:r.qaMember,
                  Checked: num(r.header?.visualCheckQty).toLocaleString(),
                  'Cri/Maj/Min': r.totals ? `${r.totals.critical}/${r.totals.major}/${r.totals.minor}` : '—',
                  Result:<Badge status={r.result||r.status}/>,
                  'Via Exception': r.viaException ? <Tag label="EXCEPTION" color={T.warning}/> : '—' }[c])}
                emptyMsg="No inspections recorded for this PO yet." />
            </Section>
          </>
        )}
      </div>
    );
  };

  return (
    <Page title="STYLE & PO" subtitle="Techpack, PO & spec extraction · quality plan · production · inspection gating">
      <TabBar tabs={TABS} active={tab} onChange={setTab} />
      {tab==='STYLES & POs'      && <StyleList />}
      {tab==='CREATE STYLE'      && <CreateStyle />}
      {tab==='TECHPACK & SPEC'   && <TechpackTab />}
      {tab==='ALLOCATION'        && <Allocation />}
      {tab==='PRODUCTION MONITOR'&& <ProductionMonitor />}
      {tab==='QUALITY PLAN'      && <QualityPlan />}
      {tab==='DAILY PRODUCTION'  && <DailyProduction />}
      {tab==='INSPECTION OFFER'  && <InspectionOffer />}
      {tab==='SUPPLIER LINKS'    && <SupplierLinks />}
      {tab==='DOCUMENTS'         && <Documents />}
      <POReviewModal />
      <Toast msg={toast?.msg} type={toast?.type} onClose={()=>setToast(null)} />
    </Page>
  );
}
